// Copyright (c) StrangeBee 2023

import React, { useEffect, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { ProviderDrawerProps } from './ProviderDrawerTypes';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import ConfigLDAP, { resetFields as resetFieldsLDAP } from './FormDirectory/LDAP';
import ConfigActiveDirectory, { resetFields as resetFieldsAD } from './FormDirectory/ActiveDirectory';
import lodash from 'lodash';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';

import './ProviderDrawer.scss';

const resetFields: Record<string, any> = {
    ad: resetFieldsAD,
    ldap: resetFieldsLDAP,
};

const DirectoryDrawer = ({
    type,
    title = type,
    enableList,
    getData,
    onSubmit,
    triggerElem,
    availableProviders,
    className,
    deps = [],
}: ProviderDrawerProps): ReactElement => {
    const local = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    let realType = type; // do not use let
    let enabledDirectory = false; // do not use let

    const enabledAd = enableList.includes('ad');
    const enableLdap = enableList.includes('ldap');

    const notAvailable = !availableProviders?.includes('ad') && !availableProviders?.includes('ldap');

    if (enabledAd || enableLdap) {
        realType = enabledAd ? 'ad' : 'ldap';
        enabledDirectory = true;
    }

    const [initialValues, setInitialeValues] = useState<Record<string, any>>({});

    const [showForm, setShowForm] = useState<boolean>(false);

    const [disabledSwitch, setDisabledSwitch] = useState(true);

    const [formType, setFormType] = useState<string>(realType);

    const computeOnFinishHandler = (toggleDrawer: any) => (state: any) => {
        const result = { ...initialValues, ...state };
        onResetForm();
        toggleDrawer();
        onSubmit(result);
    };

    const onChangeSwitch = (value: any): void => {
        setShowForm(value);
    };

    const changeDirectory = (value: string): void => {
        setFormType(value);
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        initForm(enableList);
        form.resetFields();
    };

    const initForm = (enableList: string[]) => {
        const enabledAd = enableList.includes('ad');
        const enableLdap = enableList.includes('ldap');

        const enabledDirectory = enabledAd || enableLdap;
        const realType = enabledDirectory ? (enabledAd ? 'ad' : 'ldap') : '';

        setFormType(realType);
        setShowForm(enabledDirectory);
    };

    useEffect(() => {
        form.resetFields();
    }, [initialValues]); //eslint-disable-line

    useEffect(() => {
        setShowForm(enabledDirectory || showForm);
        const newValues = {
            ...resetFields[formType],
            ...lodash.cloneDeep(getData(formType)),
            ...{ UIType: formType, UIenabled: enabledDirectory || showForm },
        };
        setInitialeValues(newValues);
    }, [formType, ...deps]); //eslint-disable-line

    useEffect(() => {
        initForm(enableList);
        setDisabledSwitch(!enableList.some((v) => ['oauth2', 'local', 'sso'].includes(v)));
        // eslint-disable-next-line
    }, [enableList]);

    return (
        <THDrawer
            title={title}
            className={classList('auth-details__providers-drawer', className)}
            triggerElem={triggerElem}
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                initForm(enableList);
                form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    onResetForm();
                                    toggleDrawer();
                                    setFieldsTouched(false);
                                }}
                                hidden={!fieldsTouched || notAvailable}
                            />
                        }
                    >
                        <THForm.Item
                            name="UIenabled"
                            label={local('configPlateform.directory.enable')}
                            valuePropName="checked"
                        >
                            <THSwitch onChange={onChangeSwitch} disabled={disabledSwitch || notAvailable} />
                        </THForm.Item>
                        {showForm && (
                            <div className="auth-form__section">
                                <TitleSection>{local('configPlateform.general.setting')}</TitleSection>
                                <THForm.Item name={'UIType'}>
                                    <THSelect
                                        placeholder="Choose directory"
                                        options={[
                                            {
                                                label: 'ldap',
                                                value: 'ldap',
                                                disabled: !availableProviders?.includes('ldap'),
                                            },
                                            { label: 'ad', value: 'ad', disabled: !availableProviders?.includes('ad') },
                                        ]}
                                        onChange={changeDirectory}
                                    />
                                </THForm.Item>
                                {formType === 'ldap' && <ConfigLDAP availableProviders={availableProviders} />}
                                {formType === 'ad' && <ConfigActiveDirectory availableProviders={availableProviders} />}
                            </div>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default DirectoryDrawer;
